import type { DatePickerProps } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import { formatDateTimeRange, getPeriodTime, parseFormattedTimeRange } from './components/EpriceTime/utils';
import { PricedisplayType } from '@/shared/types';
import { IdExpriceTime, TimeDetailVo, TimeSlot } from './type';
import { isNil } from 'lodash-es';
dayjs.extend(minMax);

// 获取从 startDateStr 开始的连续 days 天的日期数组，格式为 'YYYY-MM-DD'
export function getDateArray(startDateStr: string, days = 7) {
  const dates = [];
  const start = dayjs(startDateStr);
  for (let i = 0; i < days; i++) {
    dates.push(start.add(i, 'day').format('YYYY-MM-DD'));
  }
  return dates;
}

// 禁用日期函数
export const disabled7DaysDate = (current: Dayjs | null, { from }: { from?: Dayjs }, limitTime: boolean = true) => {
  if (!current) return false;

  const today = dayjs().startOf('day');
  const currentDate = current.startOf('day');

  // 计算当前日期是星期几（0是周日，6是周六）
  const dayOfWeek = currentDate.day();
  // 判断当前日期是否是周六
  const isSaturday = dayOfWeek === 6;
  // 判断是否是今天或之后的日期
  const isAfterToday = isNil(limitTime) || limitTime ? currentDate.isAfter(today) : true;
  if (!from) {
    // 未选择起始日期：只能选择今天及之后的周六
    return !(isSaturday && isAfterToday);
  } else {
    // 已选择起始日期：只能选起始日后第6天（即起始日是周六，加6天是下周五）
    // 注意：这里逻辑需要确认，若起始日是周六，加6天是下周五
    const rangeEnd = from.add(6, 'day').endOf('day');
    return !current.isSame(rangeEnd, 'day');
  }
};

// 根据给定的 idString 在 Tempdata 中查找对应的时间段和展示类型
export function findTimesById(idString: string, Tempdata: any[]): { times: string[]; displayType: string } | null {
  // 拆分形如 "1+1+20231001" 的 ID
  const [mediumType, ownerOrgId, areaId, dateStr] = idString.split('+');
  if (!ownerOrgId || !areaId || !dateStr) return null;

  // Step 1: 在 Tempdata 中遍历每个 medium，查找匹配的 ownerOrgId
  for (const medium of Tempdata) {
    //只对mediunId为medumType的数据进行查找
    if (medium.mediumId != mediumType) continue;
    const dept = medium.ownerOrgInfoVoList.find((d: any) => d.ownerOrgId == ownerOrgId);
    //const dept = medium.ownerOrgInfoVoList.find((d: any) => d.ownerOrgId == ownerOrgId);
    if (!dept) continue;

    // Step 2: 在 dept.areaInfoVoList 中查找匹配的 areaId
    const area = dept.areaInfoVoList.find((a: any) => a.areaId == areaId);
    if (!area) continue;

    // Step 3: 在 area.timeDetailVoList 中查找匹配的 date
    const dateObj = area.timeDetailVoList.find((d: any) => d.date == dateStr);
    if (!dateObj) continue;
    // Step 4: 返回该日期的 detailList 格式化为时间范围字符串数组
    return {
      times: dateObj.detailList.map((detail: any) =>
        formatDateTimeRange(detail.startDateTime, detail.endDateTime, dateObj.date)
      ),
      displayType: dateObj.displayType,
    };
  }

  return null;
}

/**
 * 将指定源 mediumId 的 areaInfoVoList（含 detailList）复制到多个目标 mediumId 的对应结构中
 * @param {Array} data - 原始数据数组，包含多个 { mediumId, mediumName, ownerOrgInfoVoList, ... } 对象
 * @param {number} sourceMediumId - 源介质ID，如 1（天然气）
 * @param {number[]} targetMediumIds - 目标介质ID数组，如 [2, 3, 4]（电力、蒸汽等）
 */
export function copyDetailListFromSourceToTargets(data: any, sourceMediumId: any, targetMediumIds: any) {
  // 1. 找到源对象
  const sourceMedium = data.find((item: any) => item.mediumId === sourceMediumId);
  if (!sourceMedium) return;

  // 2. 遍历每一个目标 mediumId
  targetMediumIds.forEach((targetMediumId: any) => {
    const targetMedium = data.find((item: any) => item.mediumId === targetMediumId);
    if (!targetMedium) return;

    // 3. 遍历 source 的 ownerOrgInfoVoList（供应部门）
    sourceMedium.ownerOrgInfoVoList.forEach((org1: any, orgIndex: any) => {
      const org2 = targetMedium.ownerOrgInfoVoList[orgIndex];
      if (!org2) return; // 目标没有对应的供应部门，跳过

      // 4. 遍历 areaInfoVoList（如船A、船B）
      org1.areaInfoVoList.forEach((area1: any, areaIndex: any) => {
        const area2 = org2.areaInfoVoList[areaIndex];
        if (!area2) return; // 目标没有对应的区域，跳过

        // 5. 遍历 timeDetailVoList（如 20231001, 20231002...）
        area1.timeDetailVoList.forEach((time1: any, timeIndex: any) => {
          const time2 = area2.timeDetailVoList[timeIndex];
          if (!time2) return; // 目标没有对应的时间，跳过

          // 6. 复制 detailList（时间区间数组）
          time2.detailList = [...(time1.detailList || [])];
        });
      });
    });
  });
  return data;
}

export const checkTimeSlotConflict = (
  id: string,
  Tempdata: any[],
  conflictedTime: Map<string, any>
): { mergedTimeSlots: TimeSlot[]; date: string } => {
  const result: TimeSlot[] = [];

  const [mediumIdStr, ownerOrgIdStr, areaIdStr, dateStr] = id.split('+');
  const [conflictedMediumId, conflictedOwnerOrgId, conflictedAreaId, conflictedDate] = id.split('+');
  const prev1 =
    conflictedMediumId +
    '+' +
    conflictedOwnerOrgId +
    '+' +
    conflictedAreaId +
    '+' +
    dayjs(conflictedDate, 'YYYYMMDD').subtract(1, 'day').format('YYYY-MM-DD'); //前一天
  const next1 =
    conflictedMediumId +
    '+' +
    conflictedOwnerOrgId +
    '+' +
    conflictedAreaId +
    '+' +
    dayjs(conflictedDate, 'YYYYMMDD').add(1, 'day').format('YYYY-MM-DD'); //后一天
  const medium = Tempdata.find(m => m.mediumId.toString() === mediumIdStr);
  if (!medium) return { mergedTimeSlots: [], date: '' };

  const ownerOrg = medium.ownerOrgInfoVoList.find((o: any) => o.ownerOrgId.toString() === ownerOrgIdStr);
  if (!ownerOrg) return { mergedTimeSlots: [], date: '' };

  const area = ownerOrg.areaInfoVoList.find((a: any) => a.areaId.toString() === areaIdStr);
  if (!area) return { mergedTimeSlots: [], date: '' };

  const timeDetails = area.timeDetailVoList;
  const currentIndex = timeDetails.findIndex((t: any) => t.date === dateStr);
  if (currentIndex === -1) return { mergedTimeSlots: [], date: '' };

  const startOfDay = dayjs(dateStr, 'YYYYMMDD').startOf('day');
  const endOfDay = startOfDay.add(2, 'day');

  const prev = timeDetails[currentIndex - 1];
  const next = timeDetails[currentIndex + 1];
  const overlaps: TimeSlot[] = [];
  // 前一天 → 若结束时间跨到当天
  if (prev) {
    for (const d of prev.detailList) {
      const start = dayjs(d.startDateTime);
      const end = dayjs(d.endDateTime);
      if (end.isAfter(startOfDay)) {
        overlaps.push({
          startDateTime: dayjs.max(start, startOfDay).format('YYYY-MM-DDTHH:mm:ss'),
          endDateTime: dayjs.min(end, endOfDay).format('YYYY-MM-DDTHH:mm:ss'),
        });
      }
    }
  }

  // 后一天 → 若开始时间在当天
  if (next) {
    for (const d of next.detailList) {
      const start = dayjs(d.startDateTime);
      const end = dayjs(d.endDateTime);
      if (start.isBefore(endOfDay)) {
        overlaps.push({
          startDateTime: dayjs.max(start).format('YYYY-MM-DDTHH:mm:ss'),
          endDateTime: dayjs.min(end, endOfDay).format('YYYY-MM-DDTHH:mm:ss'),
        });
      }
    }
  }
  if (prev1) {
    const prevData = conflictedTime.get(prev1);
    if (prevData) {
      console.log('prev1 :>> ', prevData);
      for (const d of prevData) {
        const start = dayjs(d.startDateTime);
        const end = dayjs(d.endDateTime);
        if (end.isAfter(startOfDay)) {
          overlaps.push({
            startDateTime: dayjs.max(start, startOfDay).format('YYYY-MM-DDTHH:mm:ss'),
            endDateTime: dayjs.min(end, endOfDay).format('YYYY-MM-DDTHH:mm:ss'),
          });
        }
      }
    }
  }
  if (next1 && next1.length > 0) {
    const nextData = conflictedTime.get(next1);
    if (nextData) {
      for (const d of nextData) {
        const start = dayjs(d.startDateTime);
        const end = dayjs(d.endDateTime);
        if (start.isBefore(endOfDay)) {
          overlaps.push({
            startDateTime: dayjs.max(start).format('YYYY-MM-DDTHH:mm:ss'),
            endDateTime: dayjs.min(end, endOfDay).format('YYYY-MM-DDTHH:mm:ss'),
          });
        }
      }
    }
  }
  result.push(...overlaps, ...(conflictedTime.get(id) || []));
  console.log('result', result);
  return {
    mergedTimeSlots: mergeTimeSlots(result),
    date: dateStr,
  };
};

function mergeTimeSlots(slots: TimeSlot[]): TimeSlot[] {
  if (slots.length === 0) return [];
  const sorted = slots.sort((a, b) => dayjs(a.startDateTime).diff(dayjs(b.startDateTime)));
  const merged: TimeSlot[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const prev = merged[merged.length - 1];
    const curr = sorted[i];

    if (dayjs(curr.startDateTime).isSameOrBefore(dayjs(prev.endDateTime))) {
      prev.endDateTime = dayjs.max(dayjs(prev.endDateTime), dayjs(curr.endDateTime)).format('YYYY-MM-DDTHH:mm:ss');
    } else {
      merged.push(curr);
    }
  }

  return merged;
}

export function batchMediumId(dateSelectIndex: string[], batchType: string[]) {
  const ids: string[] = [];
  dateSelectIndex.forEach(id => {
    if (batchType.length === 0) {
      ids.push(id);
    } else {
      batchType.forEach(type => {
        const parts = id.split('+');
        parts[0] = type;
        ids.push(parts.join('+'));
      });
    }
  });
  return ids;
}

export function getNearestSaturday() {
  const today = dayjs();
  const thisSaturday = today.day() < 6 ? today.day(6) : today.add(1, 'week').day(6);
  return thisSaturday.format('YYYY-MM-DD');
}

export function mergeMediumOrgZone(mediumData: any[], orgZoneData: any[], initDate: string[]): any[] {
  const result: any[] = [];

  mediumData.forEach(medium => {
    const { energyMediumId, energyMediumName } = medium;

    const ownerOrgInfoVoList = orgZoneData.map(org => {
      const { ownerOrgId, ownerOrgName, areaList = [] } = org;

      const areaInfoVoList = areaList.map((area: any) => {
        const { id: areaId, zoneName: areaName } = area;

        // 生成 7 天的空时间结构
        const timeDetailVoList: TimeDetailVo[] = Array.from({ length: 7 }, (_, i) => {
          return {
            date: initDate[i],
            displayType: PricedisplayType.HOUR,
            detailList: [],
          };
        });

        return { areaId, areaName, timeDetailVoList };
      });

      return { ownerOrgId, ownerOrgName, areaInfoVoList };
    });

    result.push({
      mediumId: energyMediumId,
      mediumName: energyMediumName,
      ownerOrgInfoVoList,
    });
  });
  return result;
}

export function clearDataByids(prevData: any, ids: string[]) {
  const newData = prevData.map((medium: any) => {
    const newOwnerOrgInfoVoList = medium.ownerOrgInfoVoList.map((dept: any) => {
      const newAreaInfoVoList = dept.areaInfoVoList.map((area: any) => {
        const newTimeDetailVoList = area.timeDetailVoList.map((date: any) => {
          const currentId = `${medium.mediumId}+${dept.ownerOrgId}+${area.areaId}+${date.date}`;
          if (ids.includes(currentId)) {
            return {
              ...date,
              detailList: [],
            };
          }
          return date;
        });
        return {
          ...area,
          timeDetailVoList: newTimeDetailVoList,
        };
      });
      return {
        ...dept,
        areaInfoVoList: newAreaInfoVoList,
      };
    });
    return {
      ...medium,
      ownerOrgInfoVoList: newOwnerOrgInfoVoList,
    };
  });
  return newData;
}

export function editTimeByIds(data: any, ids: string[], ExprieceTimeListRef: IdExpriceTime) {
  let newData = data.map((medium: any) => {
    const newOwnerOrgInfoVoList = medium.ownerOrgInfoVoList.map((dept: any) => {
      const newAreaInfoVoList = dept.areaInfoVoList.map((area: any) => {
        const newTimeDetailVoList = area.timeDetailVoList.map((date: any) => {
          const currentId = `${medium.mediumId}+${dept.ownerOrgId}+${area.areaId}+${date.date}`;
          if (ids.includes(currentId)) {
            return {
              ...date,
              displayType: ExprieceTimeListRef.displayType,
              detailList: ExprieceTimeListRef.times.map(t => {
                const { startDateTime, endDateTime } = parseFormattedTimeRange(
                  getPeriodTime(t, ExprieceTimeListRef.displayType!),
                  date.date
                );
                return { startDateTime, endDateTime };
              }),
            };
          }
          return date;
        });
        return {
          ...area,
          timeDetailVoList: newTimeDetailVoList,
        };
      });
      return {
        ...dept,
        areaInfoVoList: newAreaInfoVoList,
      };
    });
    return {
      ...medium,
      ownerOrgInfoVoList: newOwnerOrgInfoVoList,
    };
  });
  return newData;
}

export function dateToData(prevData: any, newDateRange: string[]) {
  return prevData.map((medium: any) => ({
    ...medium,
    ownerOrgInfoVoList: medium.ownerOrgInfoVoList.map((dept: any) => ({
      ...dept,
      areaInfoVoList: dept.areaInfoVoList.map((area: any) => ({
        ...area,
        timeDetailVoList: area.timeDetailVoList.map((detail: any, index: number) => ({
          ...detail,
          date: newDateRange[index] || detail.date,
          detailList: [],
        })),
      })),
    })),
  }));
}
